import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { TrendingUp, Eye, Search, AlertTriangle, Sparkles } from "lucide-react";
import { adminDashboard, adminWeeklyHeadline } from "../../lib/adminApi";

export default function AdminDashboard() {
  const [data, setData] = useState(null);
  const [aiHeadline, setAiHeadline] = useState(null);
  const [loadingAi, setLoadingAi] = useState(false);

  useEffect(() => { adminDashboard().then(setData); }, []);

  const draftHeadline = async () => {
    setLoadingAi(true);
    try { const r = await adminWeeklyHeadline(); setAiHeadline(r.headline); }
    catch (e) { setAiHeadline("AI headline failed"); }
    setLoadingAi(false);
  };

  if (!data) return <div className="p-10 text-white/40">Reading the room...</div>;

  return (
    <div className="px-5 md:px-10 py-8 max-w-7xl space-y-10" data-testid="admin-dashboard">
      <header>
        <p className="overline text-[#C17F59] mb-2">Tonight at BENO</p>
        <h1 className="font-display text-4xl md:text-5xl leading-tight">The Operating Room.</h1>
        <p className="text-white/60 max-w-2xl mt-2 text-sm">What guests want, what the market is saying, what BENO should do next. Updated continuously.</p>
      </header>

      {/* Headline signal card */}
      <section className="bg-[#15171A] border border-[#C17F59]/30 p-6 md:p-8 relative overflow-hidden" data-testid="signal-headline">
        <div className="absolute -top-20 -right-20 w-80 h-80 rounded-full pointer-events-none opacity-50"
             style={{ background: "radial-gradient(circle, rgba(193,127,89,0.15), transparent 70%)" }} />
        <div className="relative">
          <p className="overline text-[#C17F59] mb-3 flex items-center gap-2"><TrendingUp size={14} /> This Week's Gin Signal</p>
          <p className="font-display text-3xl md:text-4xl leading-snug max-w-4xl">{aiHeadline || data.headline}</p>
          <button onClick={draftHeadline} disabled={loadingAi}
                  className="mt-5 inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-[#C17F59] hover:text-[#D89A74] disabled:opacity-50"
                  data-testid="ai-draft-headline">
            <Sparkles size={14} /> {loadingAi ? "Drafting..." : "Draft AI editorial headline"}
          </button>
        </div>
      </section>

      {/* KPI grid */}
      <section className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="admin-kpis">
        <Kpi label="Gins Live" value={data.totals.gins} />
        <Kpi label="Countries" value={data.totals.countries} />
        <Kpi label="Botanicals" value={data.totals.botanicals} />
        <Kpi label="Events · 14d" value={data.totals.events_14d} accent />
        <Kpi label="Missing Image" value={data.totals.missing_image} warn={data.totals.missing_image > 0} />
        <Kpi label="ABV Unverified" value={data.totals.abv_unverified} warn={data.totals.abv_unverified > 30} />
      </section>

      {/* Three intelligence columns */}
      <section className="grid grid-cols-1 lg:grid-cols-3 gap-5" data-testid="dashboard-intel-grid">
        <Card title="What guests viewed" overline="Most-Viewed Gins" icon={<Eye size={14} />}>
          {data.intel.most_viewed.length === 0 && <p className="text-sm text-white/40">No views yet.</p>}
          {data.intel.most_viewed.slice(0, 6).map(([id, c]) => (
            <Row key={id} left={id} right={`${c}×`} />
          ))}
        </Card>
        <Card title="What guests searched" overline="Top Search Terms" icon={<Search size={14} />}>
          {data.intel.most_searched.length === 0 && <p className="text-sm text-white/40">No searches yet.</p>}
          {data.intel.most_searched.slice(0, 6).map(([q, c]) => (
            <Row key={q} left={`"${q}"`} right={`${c}×`} />
          ))}
        </Card>
        <Card title="Most-used filters" overline="Filter Mood" icon={<TrendingUp size={14} />}>
          {data.intel.most_filters.length === 0 && <p className="text-sm text-white/40">No filters used yet.</p>}
          {data.intel.most_filters.slice(0, 6).map(([f, c]) => (
            <Row key={f} left={f.replace(":", " · ")} right={`${c}×`} />
          ))}
        </Card>
      </section>

      {/* What BENO should do — alerts top 6 */}
      <section data-testid="dashboard-alerts">
        <div className="flex items-end justify-between mb-4">
          <div>
            <p className="overline text-[#C17F59] mb-1">What BENO should do next</p>
            <h2 className="font-display text-3xl">Top action signals.</h2>
          </div>
          <Link to="/admin/alerts" className="text-xs uppercase tracking-[0.25em] text-white/60 hover:text-[#C17F59] transition-colors">
            See all alerts ({data.alerts.length}) →
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {data.alerts.slice(0, 6).map((a, i) => <AlertRow key={i} a={a} />)}
        </div>
      </section>

      {/* Menu gaps preview */}
      {data.gaps.length > 0 && (
        <section data-testid="dashboard-gaps">
          <div className="flex items-end justify-between mb-4">
            <div>
              <p className="overline text-[#C17F59] mb-1">Menu Gaps</p>
              <h2 className="font-display text-3xl">Where the shelf is thin.</h2>
            </div>
            <Link to="/admin/menu-gaps" className="text-xs uppercase tracking-[0.25em] text-white/60 hover:text-[#C17F59] transition-colors">
              Full gap analysis →
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {data.gaps.slice(0, 6).map((g, i) => (
              <div key={i} className="bg-[#15171A] border border-white/8 p-4">
                <p className="overline text-[#C17F59] mb-1">{g.severity}</p>
                <p className="font-display text-xl mb-1">{g.label}</p>
                <p className="text-sm text-white/55">{g.detail}</p>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

function Kpi({ label, value, accent, warn }) {
  return (
    <div className={`bg-[#15171A] border p-4 ${warn ? "border-yellow-700/50" : accent ? "border-[#C17F59]/40" : "border-white/8"}`}>
      <p className="overline text-white/40">{label}</p>
      <p className={`font-display text-3xl mt-1 ${accent ? "text-[#C17F59]" : ""}`}>{value}</p>
    </div>
  );
}

function Card({ title, overline, icon, children }) {
  return (
    <div className="bg-[#15171A] border border-white/8 p-5 min-h-[260px]">
      <p className="overline text-[#C17F59] mb-2 flex items-center gap-2">{icon}{overline}</p>
      <h3 className="font-display text-2xl mb-4">{title}</h3>
      <div className="space-y-2.5">{children}</div>
    </div>
  );
}

function Row({ left, right }) {
  return (
    <div className="flex items-center justify-between py-1.5 border-b border-white/4 last:border-0">
      <span className="text-sm text-white/85 truncate">{left}</span>
      <span className="text-xs text-[#C17F59] tabular-nums shrink-0 ml-2">{right}</span>
    </div>
  );
}

function AlertRow({ a }) {
  const sev = a.severity === "high" ? "text-red-400" : a.severity === "medium" ? "text-yellow-400" : "text-white/50";
  return (
    <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
                className="bg-[#15171A] border border-white/8 p-4 flex items-start gap-3">
      <AlertTriangle size={16} className={`${sev} shrink-0 mt-0.5`} />
      <div className="flex-1 min-w-0">
        <p className={`overline ${sev} mb-1`}>{a.severity || "low"} · {a.type}</p>
        <p className="text-sm text-white/85 leading-snug">{a.title}</p>
        {a.detail && <p className="text-xs text-white/50 mt-1">{a.detail}</p>}
      </div>
    </motion.div>
  );
}
